import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { screensData } from '../data/productData';
import ImageModal from './ImageModal';
import '../FishFinderBuilder.css';
import styled from 'styled-components';
import { createPortal } from 'react-dom';

function QuickScreenComparison() {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedScreen, setSelectedScreen] = useState(null);
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);
  const [filters, setFilters] = useState({
    screenSize: [],
    viewOnPhone: false,
    hdmiOut: false,
    hdmiIn: false,
    networkPorts: 'any',
    bncVideoIn: false,
    controlMethod: 'any'
  });
  const [modalImage, setModalImage] = useState({ isOpen: false, url: '', alt: '' });
  const [filteredScreens, setFilteredScreens] = useState(screensData);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Get the previous builder state from location
  const builderState = location.state || {};

  // Filter options
  const screenSizes = [...new Set(screensData.map(screen => screen.screenSize))].sort((a, b) => a - b);
  const controlMethods = [...new Set(screensData.map(screen => screen.controlMethod))];

  useEffect(() => {
    filterScreens();
  }, [filters]);

  const filterScreens = () => {
    let filtered = screensData.filter(screen => {
      if (filters.screenSize.length > 0 && !filters.screenSize.includes(screen.screenSize)) return false;
      if (filters.viewOnPhone && screen.viewOnPhone !== "Yes") return false;
      if (filters.hdmiOut && screen.hdmiOut !== "Yes") return false;
      if (filters.hdmiIn && screen.hdmiIn !== "Yes") return false;
      if (filters.networkPorts === 'two' && parseInt(screen.networkPorts) < 2) return false;
      if (filters.networkPorts === 'one' && parseInt(screen.networkPorts) < 1) return false;
      if (filters.bncVideoIn && screen.bncVideoIn !== "Yes") return false;
      if (filters.controlMethod !== 'any' && screen.controlMethod !== filters.controlMethod) return false;
      return true;
    });
    setFilteredScreens(filtered);
  };

  const handleScreenSizeToggle = (size) => {
    setFilters(prev => ({
      ...prev,
      screenSize: prev.screenSize.includes(size)
        ? prev.screenSize.filter(s => s !== size)
        : [...prev.screenSize, size]
    }));
  };

  const handleScreenSelect = (screen) => {
    setSelectedScreen(screen);
    navigate('/custom-builder', {
      state: {
        ...builderState,
        selectedScreen: screen
      }
    });
  };

  const openImageModal = (url, alt) => {
    setModalImage({ isOpen: true, url, alt });
  };

  const toggleCardExpansion = (screenId, event) => {
    // Prevent the click from triggering parent elements
    event.stopPropagation();
    
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(screenId)) {
        newSet.delete(screenId);
      } else {
        newSet.add(screenId);
      }
      return newSet;
    });
  };

  // Helper function to render feature status
  const renderFeatureStatus = (value) => {
    if (value === "Yes") {
      return <span style={{ color: '#ffffff', fontWeight: 500, fontSize: '0.9em' }}>✓ Supported</span>;
    } else if (value === "No") {
      return <span style={{ color: '#b71c1c', fontWeight: 500, fontSize: '0.9em' }}>✗ Not Supported</span>;
    } else if (typeof value === 'number') {
      return <span style={{ color: '#ffffff', fontWeight: 500, fontSize: '0.9em' }}>{value}</span>;
    }
    return <span style={{ color: '#b71c1c', fontWeight: 500, fontSize: '0.9em' }}>✗ Not Included</span>;
  };

  // Tooltip component
  const Tooltip = ({ text }) => {
    const [visible, setVisible] = useState(false);
    const iconRef = useRef(null);
    const [coords, setCoords] = useState({ top: 0, left: 0 });

    useEffect(() => {
      if (visible && iconRef.current) {
        const rect = iconRef.current.getBoundingClientRect();
        setCoords({
          top: rect.bottom + window.scrollY + 6, // 6px gap
          left: rect.left + window.scrollX + rect.width / 2,
        });
      }
    }, [visible]);

    return (
      <>
        <span
          ref={iconRef}
          style={{ position: 'relative', display: 'inline-block', cursor: 'pointer' }}
          onMouseEnter={() => setVisible(true)}
          onMouseLeave={() => setVisible(false)}
          tabIndex={0}
          onFocus={() => setVisible(true)}
          onBlur={() => setVisible(false)}
        >
          <span style={{ color: '#4CAF50', marginLeft: 4, fontWeight: 'bold' }}>ⓘ</span>
        </span>
        {visible && createPortal(
          <span style={{
            minWidth: '180px',
            maxWidth: '260px',
            backgroundColor: '#222',
            color: '#fff',
            textAlign: 'left',
            borderRadius: '6px',
            padding: '8px',
            position: 'absolute',
            zIndex: 2000,
            top: coords.top,
            left: coords.left,
            transform: 'translateX(-50%)',
            opacity: 1,
            fontSize: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            whiteSpace: 'normal',
            pointerEvents: 'none',
          }}>
            {text}
          </span>,
          document.body
        )}
      </>
    );
  };

  // Function to toggle filters panel
  const toggleFilters = () => {
    setIsFiltersPanelOpen(!isFiltersPanelOpen);
  };

  const GridContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    width: 100%;
    max-width: 100%;
    margin: 0 auto;
    padding: 20px 0;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  `;

  const ImageContainer = styled.div`
    width: 100%;
    height: 80px;
    background-color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    position: relative;

    @media (min-width: 769px) {
      height: 100px;
    }
  `;

  const ProductImage = styled.img`
    width: 85%;
    height: 85%;
    object-fit: contain;

    @media (min-width: 769px) {
      width: 95%;
      height: 95%;
    }
  `;

  return (
    <div style={{ 
      width: '100%', 
      maxWidth: '100%',
      margin: 0,
      padding: 0,
      backgroundColor: '#000000',
      overflow: 'hidden'
    }}>
      {/* Back to Builder button - outside and above everything else */}
      <div style={{
        backgroundColor: '#000000',
        padding: '10px 0 0 0',
        paddingLeft: '24px'
      }}>
        <button
          onClick={() => navigate('/custom-builder', { state: builderState })}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            background: 'transparent',
            border: 'none',
            color: '#fff',
            fontSize: '14px',
            cursor: 'pointer',
            padding: '0 0 10px 0',
            width: 'fit-content'
          }}
        >
          ← Back to Builder
        </button>
      </div>

      {/* Main Content */}
      <div style={{ 
        display: 'flex',
        minHeight: 'calc(100vh - 80px)',
        margin: 0,
        padding: 0,
        width: '100%',
        backgroundColor: '#000000'
      }}>
        {/* Filters Sidebar */}
        <div style={{
          width: '25%',
          backgroundColor: '#1a1a1a',
          borderRight: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '2px 0 8px rgba(0, 0, 0, 0.2)',
          position: 'sticky',
          top: '0',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          padding: '10px 15px',
          margin: 0
        }}>
          <h3 style={{ 
            fontSize: '14px',
            margin: '0 0 10px 0',
            color: '#ffffff',
            fontWeight: '600'
          }}>Filter Options</h3>
          
          <div style={{
            overflowY: 'auto',
            padding: '0 0 10px 0',
            flex: 1
          }}>
            <div className="filter-section" style={{ 
              marginBottom: '8px',
              paddingBottom: '8px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <h4 style={{ 
                fontSize: '12px',
                marginBottom: '4px',
                color: '#b8c7d9',
                fontWeight: '500'
              }}>Screen Size</h4>
              {screenSizes.map(size => (
                <label key={size} className="filter-checkbox" style={{
                  fontSize: '12px',
                  marginBottom: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  color: '#e0e0e0',
                  cursor: 'pointer'
                }}>
                  <input
                    type="checkbox"
                    checked={filters.screenSize.includes(size)}
                    onChange={() => handleScreenSizeToggle(size)}
                    style={{
                      width: '12px',
                      height: '12px',
                      cursor: 'pointer'
                    }}
                  />
                  {size}″
                </label>
              ))}
            </div>

            <div className="filter-section" style={{ 
              marginBottom: '8px',
              paddingBottom: '8px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <h4 style={{ 
                fontSize: '12px',
                marginBottom: '4px',
                color: '#b8c7d9',
                fontWeight: '500'
              }}>Features</h4>
              <label className="filter-checkbox" style={{
                fontSize: '12px',
                marginBottom: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: '#e0e0e0',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={filters.viewOnPhone}
                  onChange={(e) => setFilters(prev => ({ ...prev, viewOnPhone: e.target.checked }))}
                  style={{
                    width: '12px',
                    height: '12px',
                    cursor: 'pointer'
                  }}
                />
                View on Phone
              </label>
              <label className="filter-checkbox" style={{
                fontSize: '12px',
                marginBottom: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: '#e0e0e0',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={filters.hdmiOut}
                  onChange={(e) => setFilters(prev => ({ ...prev, hdmiOut: e.target.checked }))}
                  style={{
                    width: '12px',
                    height: '12px',
                    cursor: 'pointer'
                  }}
                />
                HDMI Out
              </label>
              <label className="filter-checkbox" style={{
                fontSize: '12px',
                marginBottom: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: '#e0e0e0',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={filters.hdmiIn}
                  onChange={(e) => setFilters(prev => ({ ...prev, hdmiIn: e.target.checked }))}
                  style={{
                    width: '12px',
                    height: '12px',
                    cursor: 'pointer'
                  }}
                />
                HDMI In
              </label>
              <label className="filter-checkbox" style={{
                fontSize: '12px',
                marginBottom: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                color: '#e0e0e0',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={filters.bncVideoIn}
                  onChange={(e) => setFilters(prev => ({ ...prev, bncVideoIn: e.target.checked }))}
                  style={{
                    width: '12px',
                    height: '12px',
                    cursor: 'pointer'
                  }}
                />
                BNC Video In
              </label>
            </div>

            <div className="filter-section" style={{ 
              marginBottom: '8px',
              paddingBottom: '8px',
              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <h4 style={{ 
                fontSize: '12px',
                marginBottom: '4px',
                color: '#b8c7d9',
                fontWeight: '500'
              }}>Network Ports</h4>
              <select
                value={filters.networkPorts}
                onChange={(e) => setFilters(prev => ({ ...prev, networkPorts: e.target.value }))}
                style={{
                  fontSize: '12px',
                  padding: '2px 4px',
                  width: '100%',
                  height: '24px',
                  backgroundColor: '#262626',
                  color: '#e0e0e0',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                <option value="any">Any</option>
                <option value="one">At least 1</option>
                <option value="two">At least 2</option>
              </select>
            </div>

            <div className="filter-section" style={{ 
              marginBottom: '8px',
              paddingBottom: '8px'
            }}>
              <h4 style={{ 
                fontSize: '12px',
                marginBottom: '4px',
                color: '#b8c7d9',
                fontWeight: '500'
              }}>Control Method</h4>
              <select
                value={filters.controlMethod}
                onChange={(e) => setFilters(prev => ({ ...prev, controlMethod: e.target.value }))}
                style={{
                  fontSize: '12px',
                  padding: '2px 4px',
                  width: '100%',
                  height: '24px',
                  backgroundColor: '#262626',
                  color: '#e0e0e0',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                <option value="any">Any</option>
                {controlMethods.map(method => (
                  <option key={method} value={method}>{method}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div style={{
          width: '75%',
          padding: '0 20px 20px 20px',
          backgroundColor: '#000000'
        }}>
          <h2 style={{
            color: '#ffffff',
            fontSize: '22px',
            marginBottom: '10px',
            marginTop: '0px'
          }}>Screen Selection</h2>
          {filteredScreens.length === 0 ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px 20px',
              color: '#b8c7d9',
              textAlign: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '8px',
              margin: '20px 0'
            }}>
              <span style={{ fontSize: '24px', marginBottom: '12px' }}>🔍</span>
              <h3 style={{ 
                margin: '0 0 8px 0',
                color: '#ffffff',
                fontSize: '18px'
              }}>No Matching Screens Found</h3>
              <p style={{ 
                margin: '0',
                fontSize: '14px',
                maxWidth: '400px',
                lineHeight: '1.5'
              }}>
                Try adjusting your filters to see more options. Consider changing the screen size or feature requirements.
              </p>
            </div>
          ) : (
            <GridContainer>
              {filteredScreens.map(screen => (
                <div key={screen.id} style={{
                  backgroundColor: '#1a1a1a',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  width: '100%'
                }}>
                  {/* White container for image */}
                  <ImageContainer onClick={() => openImageModal(screen.image, screen.name)}>
                    <ProductImage 
                      src={screen.image} 
                      alt={screen.name}
                    />
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <svg 
                        width="14" 
                        height="14" 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="white" 
                        strokeWidth="2" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      >
                        <circle cx="11" cy="11" r="8"></circle>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                      </svg>
                    </div>
                  </ImageContainer>

                  {/* Rest of the card content */}
                  <div style={{ padding: '8px' }}>
                    <h3 style={{ fontSize: '0.85em', margin: '0 0 4px 0', color: '#fff' }}>{screen.name}</h3>
                    <p style={{ fontSize: '0.85em', margin: '0 0 4px 0', color: '#4CAF50', display: 'flex', alignItems: 'center', gap: 4 }}>
                      ${screen.msrp.toLocaleString()}
                      <Tooltip text={'💰 This may not be the best price! Select your components, then click Find Best Price on the bundle builder page.'} />
                    </p>
                    <div className="screen-specs" style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: '0',
                      fontSize: '0.9em'
                    }}>
                      <div className="spec-row" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '4px 0',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Screen Size:</span>
                        <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em' }}>{screen.screenSize}″</span>
                      </div>
                      <div className="spec-row" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '4px 0',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Control Method:</span>
                        <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em' }}>{screen.controlMethod}</span>
                      </div>
                      <div className="spec-row" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '4px 0',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Resolution:</span>
                        <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em' }}>{screen.resolution || "Not specified"}</span>
                      </div>
                      <div className="spec-row" style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '4px 0',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>View on Phone:</span>
                        {renderFeatureStatus(screen.viewOnPhone)}
                      </div>

                      {/* Expandable section */}
                      <div className={`expandable-specs ${expandedCards.has(screen.id) ? 'expanded' : ''}`} style={{ 
                        margin: 0, 
                        padding: 0,
                        borderTop: 'none',
                        marginTop: 0
                      }}>
                        {expandedCards.has(screen.id) ? (
                          <>
                            <div className="spec-row" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 0',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                              <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Avg Power Draw @ 12V:</span>
                              <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em' }}>{screen.powerDraw12V || "Not specified"} A</span>
                            </div>
                            <div className="spec-row" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 0',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                              <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Network Ports:</span>
                              <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em' }}>{screen.networkPorts}</span>
                            </div>
                            <div className="spec-row" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 0',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                              <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>HDMI Out:</span>
                              {renderFeatureStatus(screen.hdmiOut)}
                            </div>
                            <div className="spec-row" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 0',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                              <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>HDMI In:</span>
                              {renderFeatureStatus(screen.hdmiIn)}
                            </div>
                            <div className="spec-row" style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '4px 0',
                              borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                            }}>
                              <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>BNC Video In:</span>
                              {renderFeatureStatus(screen.bncVideoIn)}
                            </div>
                          </>
                        ) : (
                          <div className="spec-row" style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '4px 0',
                            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                            opacity: 0.6
                          }}>
                            <span className="spec-label" style={{ color: '#b8c7d9', fontSize: '0.9em' }}>Network Ports:</span>
                            <span className="spec-value" style={{ color: '#ffffff', fontSize: '0.9em' }}>{screen.networkPorts}</span>
                          </div>
                        )}
                        
                        <button 
                          className="expand-button"
                          onClick={(e) => toggleCardExpansion(screen.id, e)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#b8c7d9',
                            width: '100%',
                            padding: '4px 0',
                            marginTop: '4px',
                            fontSize: '0.9em',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          {expandedCards.has(screen.id) ? (
                            <>Show Less <span style={{ fontSize: '0.8em', color: '#b8c7d9' }}>▲</span></>
                          ) : (
                            <>Show More Specs <span style={{ fontSize: '0.8em', color: '#b8c7d9' }}>▼</span></>
                          )}
                        </button>
                      </div>
                    </div>
                    <button 
                      className="select-button"
                      onClick={() => handleScreenSelect(screen)}
                      style={{
                        width: '100%',
                        padding: '3px',
                        fontSize: '0.7em',
                        marginTop: '4px'
                      }}
                    >
                      Select Screen
                    </button>
                  </div>
                </div>
              ))}
            </GridContainer>
          )}
        </div>
      </div>

      {/* Image Modal */}
      {modalImage.isOpen && (
        <ImageModal
          isOpen={modalImage.isOpen}
          imageUrl={modalImage.url}
          altText={modalImage.alt}
          onClose={() => setModalImage({ isOpen: false, url: '', alt: '' })}
        />
      )}
    </div>
  );
}

// Add these styles to your CSS file
const styles = `
@media (max-width: 768px) {
  .screen-comparison-layout {
    display: block;
  }

  .filters-sidebar {
    width: 100%;
    max-width: none;
  }

  .screens-grid {
    margin-left: 0;
  }

  .mobile-filters-button {
    display: flex !important;
  }
}

@media (min-width: 769px) {
  .mobile-filters-button {
    display: none !important;
  }
}
`;

export default QuickScreenComparison; 